import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";

const destinations = [
  {
    name: "USA",
    tagline: "Statue of Liberty · Times Square",
    img: "https://images.unsplash.com/photo-1664353655151-9d94a9170eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwc2t5bGluZSUyMG5pZ2h0fGVufDB8fHx8MTc3ODY1Nzk2MHww&ixlib=rb-4.1.0&q=85",
    accent: "navy",
    days: "8N · 9D",
    from: "₹ 1,49,000",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    name: "Dubai",
    tagline: "Burj Khalifa · Desert Safari",
    img: "https://images.unsplash.com/photo-1749849934727-bc57c0bd9dd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGx1eHVyeSUyMG5pZ2h0fGVufDB8fHx8MTc3ODY1Nzk2MHww&ixlib=rb-4.1.0&q=85",
    accent: "gold",
    days: "4N · 5D",
    from: "₹ 54,000",
    span: "",
  },
  {
    name: "Europe",
    tagline: "Paris · Rome · Amsterdam",
    img: "https://images.unsplash.com/photo-1642947392578-b37fbd9a4d45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwYXJpcyUyMGVpZmZlbCUyMHRvd2VyJTIwbmlnaHR8ZW58MHx8fHwxNzc4NjU3OTYwfDA&ixlib=rb-4.1.0&q=85",
    accent: "sky",
    days: "10N · 11D",
    from: "₹ 1,89,000",
    span: "",
  },
  {
    name: "Thailand",
    tagline: "Bangkok · Phuket · Krabi",
    img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?crop=entropy&cs=srgb&fm=jpg&w=1080&q=85",
    accent: "gold",
    days: "5N · 6D",
    from: "₹ 38,000",
    span: "",
  },
  {
    name: "Bali",
    tagline: "Ubud · Seminyak · Nusa Dua",
    img: "https://images.unsplash.com/photo-1729606558546-4c405403a41a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBiYWxpfGVufDB8fHx8MTc3ODY1Nzk2MHww&ixlib=rb-4.1.0&q=85",
    accent: "sky",
    days: "6N · 7D",
    from: "₹ 62,000",
    span: "",
  },
  {
    name: "Singapore",
    tagline: "Marina Bay · Sentosa Island",
    img: "https://images.unsplash.com/photo-1693564872467-e45887e7aedd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxzaW5nYXBvcmUlMjBtYXJpbmElMjBiYXklMjBzYW5kcyUyMG5pZ2h0fGVufDB8fHx8MTc3ODY1Nzk2MHww&ixlib=rb-4.1.0&q=85",
    accent: "navy",
    days: "4N · 5D",
    from: "₹ 71,000",
    span: "",
  },
];

const accentMap = {
  navy: "#003366",
  sky: "#0099CC",
  gold: "#D4AF37",
};

export default function Destinations() {
  return (
    <section id="destinations" data-testid="destinations-section" className="relative py-24 md:py-32 bg-offwhite">
      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold mb-4">— Curated Journeys</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-2xl leading-[1.05] text-navy-deep">
              Popular <span className="text-gradient-navy">destinations</span><br/>around the globe.
            </h2>
          </div>
          <p className="max-w-md text-slate-600 text-base leading-relaxed">
            Handpicked tour packages built around premium stays, private transfers, and visa-ready itineraries.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-5 auto-rows-[260px] lg:auto-rows-[300px]">
          {destinations.map((d, i) => (
            <motion.button
              key={d.name}
              data-testid={`destination-${d.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative tilt-card glow-border rounded-3xl overflow-hidden text-left soft-shadow bg-white ${d.span}`}
            >
              <img
                src={d.img}
                alt={d.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/95 via-navy-deep/45 to-transparent" />

              <div className="relative h-full p-6 md:p-7 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur">
                    <MapPin className="w-3 h-3" style={{ color: accentMap[d.accent] }} />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-navy-deep">{d.days}</span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-2 group-hover:translate-x-0 shadow-xl">
                    <ArrowUpRight className="w-4 h-4 text-navy-deep" />
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] mb-2 font-semibold" style={{ color: d.accent === "gold" ? "#FFD700" : "#7DD3FC" }}>
                    {d.tagline}
                  </div>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold leading-none mb-3 text-white">{d.name}</h3>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/85">
                      <span className="text-white/60">From</span> <span className="text-white font-semibold">{d.from}</span>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white font-medium">View Itinerary →</div>
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
